/**
 * How a declared value reads on screen: by its kind (a manifest column's,
 * or one read off a request schema's type, format and `x-cis-field`), never
 * by the field's name.
 */
import {
  formatDateOrDash,
  formatDateTimeOrDash,
  formatQuotedPrice,
} from "../formatters";
import type {
  FileValue,
  JsonSchema,
  ManifestColumn,
  ManifestObject,
  StateTone,
  ValueKind,
} from "./contract";

function typeOf(schema: JsonSchema): string | undefined {
  if (Array.isArray(schema.type)) return schema.type.find((t) => t !== "null");
  if (schema.type) return schema.type;
  const branch = schema.anyOf?.find((s) => s.type !== "null");
  return branch ? typeOf(branch) : undefined;
}

/** The schema with any nullable `anyOf` wrapper unwrapped. */
export function effectiveSchema(schema: JsonSchema): JsonSchema {
  const branch = schema.anyOf?.find((s) => s.type !== "null");
  return branch
    ? { ...branch, ...schema, anyOf: undefined, type: branch.type }
    : schema;
}

/** `FileValue`'s properties, sorted: a field (or a list's items) of exactly this shape is a file, as the manifest build reads it. */
const FILE_VALUE_FIELDS = ["href", "name", "size_bytes"];

function isFileValueSchema(schema: JsonSchema): boolean {
  const s = effectiveSchema(schema);
  const inner = typeOf(s) === "array" && s.items ? effectiveSchema(s.items) : s;
  const fields = Object.keys(inner.properties ?? {}).sort();
  return (
    typeOf(inner) === "object" &&
    fields.join(",") === FILE_VALUE_FIELDS.join(",")
  );
}

/** A schema field's kind, as the platform derives a column's (spec §4.5). */
export function valueKindOf(schema: JsonSchema): ValueKind {
  if (isFileValueSchema(schema)) return "file";
  const s = effectiveSchema(schema);
  const widget = s["x-cis-field"]?.widget;
  if (
    widget === "money" ||
    widget === "state" ||
    widget === "identifier" ||
    widget === "link" ||
    widget === "formatted_text"
  )
    return widget;
  if (s.enum) return "state";
  const type = typeOf(s);
  if (type === "boolean") return "boolean";
  if (type === "integer" || type === "number") return "number";
  if (s.format === "date") return "date";
  if (s.format === "date-time") return "datetime";
  return "text";
}

/** The tone of a state value's badge, as its object declares it: neutral where it declares none. */
export function stateToneOf(
  object: Pick<ManifestObject, "state_tones"> | null,
  value: unknown,
): StateTone {
  return object?.state_tones?.[String(value)] ?? "neutral";
}

/** "awaiting_release" → "Awaiting release": a value with no declared label. */
export function humanise(value: string): string {
  const words = value.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** An enum value's label: the declared one, else the value humanised. */
export function enumLabel(
  labels: Record<string, string> | undefined,
  value: string,
): string {
  return labels?.[value] ?? humanise(value);
}

/** The value as text; "–" when absent. */
export function formatValue(
  value: unknown,
  kind: ValueKind,
  labels?: Record<string, string>,
): string {
  if (value === null || value === undefined || value === "") return "–";
  if (kind === "file")
    return Array.isArray(value)
      ? value.map(fileName).join(", ")
      : fileName(value);
  // A text field that declares `enum_labels` without being a state (a purchase order's Stage) reads by its labels too.
  if (kind === "text" && labels && Object.keys(labels).length > 0) {
    const labelled = (v: unknown) => enumLabel(labels, String(v));
    return Array.isArray(value)
      ? value.map(labelled).join(", ")
      : labelled(value);
  }
  switch (kind) {
    case "money":
      return typeof value === "number"
        ? formatQuotedPrice(value)
        : String(value);
    case "number":
      return typeof value === "number"
        ? value.toLocaleString("en-GB")
        : String(value);
    case "date":
      return formatDateOrDash(String(value));
    case "datetime":
      return formatDateTimeOrDash(String(value));
    case "state":
      return enumLabel(labels, String(value));
    case "boolean":
      return value === true ? "Yes" : "No";
    case "identifier":
    case "link":
    case "formatted_text":
    case "text":
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
}

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A file value's name; anything else as text. */
function fileName(value: unknown): string {
  return isRecordLike(value) && typeof value.name === "string"
    ? value.name
    : String(value);
}

/**
 * The files a `file` value holds (one `FileValue` or a list) that the shell can open: those served from an API path,
 * fetched with the person's sign-in. A value that is no file, or names a URL elsewhere, is left out and shows as its
 * name only (`formatValue`). Twin of `openable_files` in `record_values.py`.
 */
export function filesOf(value: unknown): FileValue[] {
  return (Array.isArray(value) ? value : [value]).flatMap(
    (item: unknown): FileValue[] => {
      if (!isRecordLike(item)) return [];
      const { name, href, size_bytes: size } = item;
      if (typeof name !== "string" || name === "") return [];
      if (typeof href !== "string" || !href.startsWith("/")) return [];
      if (href.startsWith("//")) return [];
      const isWhole = typeof size === "number" && Number.isInteger(size);
      return [{ name, href, size_bytes: isWhole ? size : null }];
    },
  );
}

/** A link column's value as the URL to open, when it is an absolute http(s) URL; else null, so the value
 *  shows as plain text (never a `javascript:` or relative link). */
export function linkHref(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return /^https?:\/\//i.test(value.trim()) ? value.trim() : null;
}

/** A manifest column's value as text: by its kind, a state by its declared label. */
export function formatColumnValue(
  value: unknown,
  column: Pick<ManifestColumn, "kind" | "enum_labels">,
): string {
  return formatValue(value, column.kind, column.enum_labels);
}

/** A request-schema field's value as text (the table input's read-only columns). */
export function formatSchemaValue(value: unknown, schema: JsonSchema): string {
  return formatValue(
    value,
    valueKindOf(schema),
    effectiveSchema(schema)["x-cis-field"]?.enum_labels,
  );
}

/** "2026-10-17T10:30", seconds optional: a wall-clock date and time with no zone, as a `datetime-local` input has it. */
const WALL_CLOCK = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2}(\.\d+)?)?$/;

/**
 * A date-time body value as a `datetime-local` input shows it ("2026-10-17T10:30"), in the person's own time: an
 * offset-free value stands as it is; one with an offset (what the input sends, `instantOfLocalDateTime`, or a dry
 * run's prefill) is converted. Anything unreadable shows empty rather than wrong.
 */
export function localDateTimeInputValue(value: unknown): string {
  if (typeof value !== "string" || value === "") return "";
  const bare = WALL_CLOCK.exec(value);
  if (bare) return bare[1]!;
  const moment = new Date(value);
  if (Number.isNaN(moment.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}` +
    `T${pad(moment.getHours())}:${pad(moment.getMinutes())}`
  );
}

/**
 * What a `datetime-local` input's value is sent as: the moment that wall-clock time names in the person's own time,
 * as UTC ISO ("2026-11-20T13:00" in London → "2026-11-20T13:00:00.000Z"). Sent bare, the server could not tell which
 * zone was meant (a 500 on Publish quote, QT-G57317). `localDateTimeInputValue` shows it back as the time picked.
 * Null for a cleared or unreadable value.
 */
export function instantOfLocalDateTime(local: string): string | null {
  if (!WALL_CLOCK.test(local)) return null;
  // A date-time string with no offset is read as local time (ECMAScript Date.parse), which is what the input means.
  const moment = new Date(local);
  return Number.isNaN(moment.getTime()) ? null : moment.toISOString();
}
